import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Trash2, Mail, Phone, RefreshCw, Inbox, CheckCircle2, Sprout, Calendar, ClipboardPlus, MapPin } from "lucide-react";
import { toast } from "sonner";
import AdminNav from "@/components/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const STATUSES = [
  { v: "new", label: "New", color: "bg-sky/15 text-sky border-sky/30" },
  { v: "contacted", label: "Contacted", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { v: "quoted", label: "Quoted", color: "bg-violet-100 text-violet-700 border-violet-200" },
  { v: "won", label: "Won", color: "bg-crop/15 text-crop border-crop/30" },
  { v: "lost", label: "Lost", color: "bg-rose-100 text-rose-700 border-rose-200" },
];

const statusMeta = (s) => STATUSES.find((x) => x.v === s) || STATUSES[0];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, won: 0, today: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("aero_user") || "{}"); } catch { return {}; }
  })();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        api.get("/admin/leads", { params: filter !== "all" ? { status_filter: filter } : {} }),
        api.get("/admin/stats"),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("aero_token");
        navigate("/login");
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success("Status updated");
      fetchAll();
    } catch {
      toast.error("Failed to update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted");
      fetchAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const [convertLead, setConvertLead] = useState(null);
  const [convertForm, setConvertForm] = useState({ location_address: "", scheduled_date: "" });
  const [converting, setConverting] = useState(false);

  const openConvert = (lead) => {
    setConvertLead(lead);
    setConvertForm({ location_address: lead.farm_name || "", scheduled_date: "" });
  };

  const convert = async () => {
    if (!convertForm.location_address) return toast.error("Address required");
    setConverting(true);
    try {
      const { data } = await api.post(`/admin/leads/${convertLead.id}/convert`, {
        location_address: convertForm.location_address,
        scheduled_date: convertForm.scheduled_date || null,
      });
      toast.success("Work order created");
      setConvertLead(null);
      navigate(`/admin/work-orders/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Conversion failed");
    } finally { setConverting(false); }
  };

  const logout = () => {
    localStorage.removeItem("aero_token");
    localStorage.removeItem("aero_user");
    navigate("/login");
  };

  const exportCsv = () => {
    if (!leads.length) return toast.error("No leads to export");
    const headers = ["Date","Name","Farm","Phone","Email","Acreage","Crop","Service","Status","Message"];
    const rows = leads.map((l) => [
      new Date(l.created_at).toLocaleString(),
      l.name, l.farm_name || "", l.phone, l.email, l.acreage || "", l.crop_type || "", l.service || "", l.status, (l.message || "").replace(/\n/g, " "),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `aeroacres-leads-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const statCards = [
    { label: "Total leads", value: stats.total, icon: Inbox, color: "bg-primary text-primary-foreground" },
    { label: "New today", value: stats.today, icon: Calendar, color: "bg-accent text-accent-foreground" },
    { label: "Unhandled", value: stats.new, icon: Sprout, color: "bg-sky text-white" },
    { label: "Won deals", value: stats.won, icon: CheckCircle2, color: "bg-crop text-white" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-muted/30">
      <AdminNav />

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl text-primary font-bold">Leads dashboard</h1>
            <p className="text-muted-foreground mt-1">Manage every quote request from the AeroAcres website.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchAll} variant="outline" size="sm" data-testid="refresh-btn" className="rounded-full">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button onClick={exportCsv} size="sm" data-testid="export-btn" className="rounded-full bg-primary hover:bg-secondary text-primary-foreground">Export CSV</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
          {statCards.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} data-testid={`stat-${i}`} className="bg-card border border-border rounded-2xl p-5 shadow-card">
                <div className="flex items-center justify-between">
                  <div className={`h-10 w-10 rounded-xl ${s.color} flex items-center justify-center`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-4 font-display text-3xl font-bold text-primary">{s.value}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{s.label}</div>
              </div>
            );
          })}
        </div>

        <div className="bg-card border border-border rounded-3xl shadow-card overflow-hidden">
          <div className="p-5 border-b border-border flex items-center justify-between flex-wrap gap-3">
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList data-testid="status-tabs">
                <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
                {STATUSES.map((s) => (
                  <TabsTrigger key={s.v} value={s.v} data-testid={`tab-${s.v}`}>{s.label}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="text-sm text-muted-foreground">{leads.length} lead{leads.length !== 1 ? "s" : ""}</div>
          </div>

          {leads.length === 0 ? (
            <div data-testid="empty-leads" className="py-20 text-center">
              <Inbox className="h-10 w-10 mx-auto text-muted-foreground/40" />
              <div className="mt-3 font-display text-lg text-primary">No leads yet</div>
              <div className="text-sm text-muted-foreground mt-1">When farmers submit the contact form, they'll appear here.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold">Date</th>
                    <th className="text-left px-5 py-3 font-semibold">Lead</th>
                    <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">Crop / Acreage</th>
                    <th className="text-left px-5 py-3 font-semibold hidden lg:table-cell">Service</th>
                    <th className="text-left px-5 py-3 font-semibold">Status</th>
                    <th className="text-right px-5 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l, idx) => {
                    const meta = statusMeta(l.status);
                    return (
                      <tr key={l.id} data-testid={`lead-row-${idx}`} className="border-t border-border hover:bg-muted/20 transition-colors">
                        <td className="px-5 py-4 text-muted-foreground whitespace-nowrap">
                          {new Date(l.created_at).toLocaleDateString()}<br />
                          <span className="text-xs">{new Date(l.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="font-semibold text-primary">{l.name}</div>
                          {l.farm_name && <div className="text-xs text-muted-foreground">{l.farm_name}</div>}
                          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                            <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 text-primary hover:text-accent"><Phone className="h-3 w-3" /> {l.phone}</a>
                            <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 text-primary hover:text-accent"><Mail className="h-3 w-3" /> {l.email}</a>
                          </div>
                          {l.message && <div className="mt-2 text-xs text-muted-foreground max-w-md line-clamp-2">"{l.message}"</div>}
                        </td>
                        <td className="px-5 py-4 hidden md:table-cell">
                          <div className="text-foreground">{l.crop_type || "—"}</div>
                          <div className="text-xs text-muted-foreground">{l.acreage || "—"}</div>
                        </td>
                        <td className="px-5 py-4 hidden lg:table-cell text-foreground">{l.service || "—"}</td>
                        <td className="px-5 py-4">
                          <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                            <SelectTrigger data-testid={`status-select-${idx}`} className={`h-8 text-xs rounded-full border ${meta.color}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s) => (
                                <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <div className="inline-flex items-center gap-1">
                            <Button onClick={() => openConvert(l)} variant="ghost" size="sm" data-testid={`convert-lead-${idx}`} className="text-primary hover:text-accent rounded-full">
                              <ClipboardPlus className="h-4 w-4 mr-1" /> Convert
                            </Button>
                            <Button onClick={() => remove(l.id)} variant="ghost" size="icon" data-testid={`delete-lead-${idx}`} className="text-muted-foreground hover:text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Convert lead -> work order dialog */}
      <Dialog open={!!convertLead} onOpenChange={(o) => !o && setConvertLead(null)}>
        <DialogContent data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Convert to work order</DialogTitle>
          </DialogHeader>
          {convertLead && (
            <div className="space-y-4 py-2">
              <div className="text-sm">
                <div className="text-muted-foreground text-xs uppercase tracking-wider">Client</div>
                <div className="font-semibold text-primary">{convertLead.name} {convertLead.farm_name && `· ${convertLead.farm_name}`}</div>
                <div className="text-xs text-muted-foreground">{convertLead.phone} · {convertLead.email}</div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Field address *</Label>
                <Input value={convertForm.location_address} onChange={(e) => setConvertForm({ ...convertForm, location_address: e.target.value })}
                  placeholder="123 Krome Ave, Homestead FL" data-testid="convert-address" className="mt-2 h-11 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Scheduled date</Label>
                <Input type="date" value={convertForm.scheduled_date} onChange={(e) => setConvertForm({ ...convertForm, scheduled_date: e.target.value })}
                  data-testid="convert-date" className="mt-2 h-11 rounded-xl" />
              </div>
              <p className="text-xs text-muted-foreground">After conversion you'll be sent to the work order editor to add GPS, assign a pilot, and finalize details. The lead will be marked "Won".</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConvertLead(null)}>Cancel</Button>
            <Button onClick={convert} disabled={converting} data-testid="convert-submit" className="bg-primary hover:bg-secondary text-primary-foreground">
              <MapPin className="h-4 w-4 mr-1.5" /> Create work order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
